import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "@environments/environment";
import { User } from "../_interfaces/user";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  toArray,
} from "rxjs/operators";
import { Plan } from "@app/shared/_interfaces/plan";
import { skipModalError } from "@app/shared/_models/auth";
import * as dot from "dot-object";
import { chartStatusChecked, enableRestrictionFreePlans } from "@config/config";
import { Analytic } from "@app/features/analytics/analytics.interface";

@Injectable({ providedIn: "root" })
export class UserService {
  private http = inject(HttpClient);

  readonly apiUrl = environment.newApiUrl;
  private prefix = `${this.apiUrl}/user`;

  private _userInfo = new BehaviorSubject<User.Info>(null);
  readonly userInfo = this._userInfo.asObservable();

  private _userPlans = new BehaviorSubject<Plan.Item[]>(null);
  readonly userPlans = this._userPlans.asObservable();

  getInfo() {
    const cachedUserInfo = this._userInfo.getValue();
    const userInfo$ = cachedUserInfo ? of(cachedUserInfo) : this.getUserInfo();
    return forkJoin([userInfo$, this.getUserPlans()]).pipe(
      catchError((err) => {
        console.error("[getInfo] catch error", { err });
        return of(null);
      }),
    );
  }

  getUserInfo(token?: string) {
    return this.http
      .get<User.Info>(`${this.prefix}/config`, {
        context: skipModalError([404]),
      })
      .pipe(
        map((info) => {
          if (info.subaccount_config) {
            info = {
              ...info,
              ...(!info?.lang && {
                lang: "en",
              }),
              subaccount_config: {
                ...info.subaccount_config,
                logo: info.subaccount_config?.logo
                  ?.toString()
                  .includes("images/logo.png")
                  ? "/assets/img/logo.png"
                  : info.subaccount_config.logo,
              },
            };
          }
          return info;
        }),
        tap((info) => this._userInfo.next(info)),
      );
  }

  disableTooltip(tooltip_key: keyof User.InfoTooltip) {
    return this.http.put<{ tooltip: string }>(
      `${this.prefix}/disable-tooltip`,
      {
        tooltip_key,
      },
    );
  }

  updateWhitelabel(whiteLabeled: boolean) {
    return this.http
      .put<{
        whiteLabeled: boolean;
      }>(`${this.prefix}/white-labeled`, { whiteLabeled })
      .pipe(
        tap(({ whiteLabeled }) =>
          this._userInfo.next({
            ...this._userInfo.value,
            white_label: whiteLabeled,
          }),
        ),
      );
  }

  getConsumerKeys() {
    return this.http
      .get<User.ConsumerKeyData>(`${this.apiUrl}/user/consumerKeys`, {
        context: skipModalError([404]),
      })
      .pipe(
        catchError(() =>
          of<User.ConsumerKeyData>({
            count: 0,
            results: [],
          }),
        ),
      );
  }

  revokeConsumerKey(consumerKey: string) {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/user/consumerKeys/${consumerKey}`,
    );
  }

  addConsumerKey(
    label: string,
    ips: string[] = [],
    permissions: User.ApiKeyPermission[] = ["SEND_SMTP", "SEND_API", "APIS"],
  ) {
    return this.http.post<User.ConsumerKeyItemWithSecret>(
      `${this.apiUrl}/user/consumerKeys`,
      {
        label,
        ...(permissions && {
          permissions,
        }),
        ...(ips &&
          ips.length > 0 && {
            ips,
          }),
      },
    );
  }

  updateConsumerKey(
    consumerKey: string,
    label: string,
    ips: string[] = [],
    permissions: User.ApiKeyPermission[] = ["SEND_SMTP", "SEND_API", "APIS"],
  ) {
    return this.http.put<User.ConsumerKeyItemWithSecret>(
      `${this.apiUrl}/user/consumerKeys/${consumerKey}`,
      {
        label,
        ...(permissions && {
          permissions,
        }),
        ...(ips &&
          ips.length > 0 && {
            ips,
          }),
      },
    );
  }

  getUserPlans() {
    return this.http
      .get<Plan.Item[]>(`${this.apiUrl}/user/plans?free_last=1`, {
        context: skipModalError(["all"]),
      })
      .pipe(
        switchMap((plans) => plans),
        filter((plan) => plan.active),
        toArray(),
        map((plans) =>
          [...plans].sort((a, b) => (b.smtp_limit ?? 0) - (a.smtp_limit ?? 0)),
        ),
        catchError(() => of<Plan.Item[]>([])),
      );
  }

  getActivePlan() {
    return this.http
      .get<Plan.Item[]>(`${this.apiUrl}/user/plans?free_last=1`, {
        context: skipModalError(["all"]),
      })
      .pipe(
        map((plans) =>
          [...plans]
            .sort((a, b) => (b.smtp_limit ?? 0) - (a.smtp_limit ?? 0))
            .find((plan) => plan.active),
        ),
        catchError(() => of(null)),
      );
  }

  getIpDetails() {
    return this.http.get<User.Ip>(
      `${this.prefix}/ips/${this.getInfoValue("ip")}`,
    );
  }

  disableFirstLogin(firstLogin: boolean = true) {
    return this.http.put(`${this.prefix}/first-login`, {
      first_login: firstLogin,
    });
  }

  getIPS(): Observable<User.Ip[]> {
    return this.http
      .get<User.Ip[]>(`${this.prefix}/ips`, {
        context: skipModalError([404]),
      })
      .pipe(
        catchError(() => of([])),
        switchMap((ips) => ips),
        map(({ active, senderScore, accountID, ip }) => ({
          active,
          senderScore,
          accountID,
          ip,
        })),
        toArray(),
      );
  }

  getValue() {
    return this._userInfo.getValue();
  }

  getInfoValue(key: string) {
    return dot.pick(key, this._userInfo.getValue());
  }

  private getLocalStorageKey(storageKey: string) {
    return `${storageKey}-${this.getInfoValue("account_id")}`;
  }

  setLocalStorageItem(storageKey: string, value: string) {
    localStorage.setItem(this.getLocalStorageKey(storageKey), value);
  }

  getLocalStorageItem(storageKey: string, defaultValue: string = null) {
    const value = localStorage.getItem(this.getLocalStorageKey(storageKey));
    if (value === null) {
      return defaultValue;
    }
    return value;
  }

  setUserData([userInfo, userPlans]: [User.Info, Plan.Item[]]) {
    this._userInfo.next(userInfo);
    this._userPlans.next(userPlans);
  }

  getStatusesChecked(): Analytic.ChartStatusEnabled {
    let statusesCheckedItem = this.getLocalStorageItem(chartStatusChecked);
    return statusesCheckedItem ? JSON.parse(statusesCheckedItem) : {} as Analytic.ChartStatusEnabled;
  }

  setStatusCheck = (status: Analytic.StatusName, check: boolean) => {
    const statusChecked = {
      ...this.getStatusesChecked(),
      [status]: check,
    };
    this.setLocalStorageItem(chartStatusChecked, JSON.stringify(statusChecked));
  };

  isPaidAccount() {
    if (enableRestrictionFreePlans) {
      return this.getInfoValue("account_type") === "paid";
    }

    return true;
  }

  updateKeycloakProfile(id: string, data: any) {
    return this.http.put(`${this.apiUrl}/keycloak/users/${id}`, data);
  }

  getKeycloakProfile(id: string) {
    return this.http.get<{
      user: {
        attributes: {
          product_id: string[];
          billingcycle: Array<"annualy" | "monthly">;
        };
      };
    }>(`${this.apiUrl}/keycloak/users/${id}`);
  }

  getUserGeolocation(id: string) {
    return this.http.get<User.Geolocation>(
      `${this.apiUrl}/keycloak/users/${id}/geolocation`,
    );
  }
}
